import { useMemo, useState } from "react";
import type { CrmContact } from "@/types/crm";
import { Button } from "@/components/ui/button";

type MergeContactDrawerProps = {
  open: boolean;
  contacts: CrmContact[];
  onClose: () => void;
  onMerge: (payload: { primaryId: number; updates: Partial<CrmContact>; secondaryIds: number[] }) => Promise<void> | void;
};

const MERGE_FIELDS: Array<{ key: keyof CrmContact; label: string }> = [
  { key: "name", label: "Display name" },
  { key: "company_name", label: "Company / Venue" },
  { key: "contact_name", label: "Contact name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "location_city", label: "City" },
  { key: "location_region", label: "State" },
  { key: "country", label: "Country" },
  { key: "notes", label: "Notes" },
];

export function MergeContactDrawer({ open, contacts, onClose, onMerge }: MergeContactDrawerProps) {
  const [primaryId, setPrimaryId] = useState<number | null>(null);
  const [selectedFields, setSelectedFields] = useState<Record<string, number>>({});
  const [merging, setMerging] = useState(false);
  const primaryContact = useMemo(() => contacts.find((contact) => contact.id === primaryId) ?? contacts[0] ?? null, [contacts, primaryId]);

  if (!open) return null;

  const handleSelect = (field: string, contactId: number) => {
    setSelectedFields((prev) => ({ ...prev, [field]: contactId }));
  };

  const handlePrimaryChange = (contactId: number) => {
    setPrimaryId(contactId);
  };

  const handleApprove = async () => {
    if (!primaryContact) return;
    setMerging(true);
    const updates: Partial<CrmContact> = {};
    MERGE_FIELDS.forEach((field) => {
      const sourceId = selectedFields[field.key as string] ?? primaryContact.id;
      const source = contacts.find((contact) => contact.id === sourceId);
      if (source && source[field.key]) {
        updates[field.key] = source[field.key];
      }
    });
    try {
      const secondaryIds = contacts.filter((contact) => contact.id !== primaryContact.id).map((contact) => contact.id);
      await onMerge({ primaryId: primaryContact.id, updates, secondaryIds });
    } finally {
      setMerging(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="h-full w-full max-w-4xl overflow-y-auto border-l border-border bg-background p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-300">Merge contacts</p>
            <h2 className="text-2xl font-semibold text-foreground">Resolve duplicates</h2>
            <p className="text-sm text-muted-foreground">Choose canonical values before approving the merge.</p>
          </div>
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        {contacts.length < 2 ? (
          <p className="mt-6 text-sm text-muted-foreground">Select at least two contacts to merge.</p>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap gap-3">
              {contacts.map((contact) => (
                <label key={contact.id} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-sm text-foreground">
                  <input
                    type="radio"
                    name="primary-contact"
                    className="h-4 w-4 accent-amber-400"
                    checked={(primaryContact?.id ?? contacts[0].id) === contact.id}
                    onChange={() => handlePrimaryChange(contact.id)}
                  />
                  Primary: {contact.name || contact.company_name || `Contact #${contact.id}`}
                </label>
              ))}
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-900/70 bg-background/60">
              <table className="w-full text-sm text-foreground/70">
                <thead>
                  <tr>
                    <th className="p-2 text-left text-xs uppercase tracking-wide text-muted-foreground">Field</th>
                    {contacts.map((contact) => (
                      <th key={contact.id} className="p-2 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        {contact.name || contact.company_name || `Contact #${contact.id}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MERGE_FIELDS.map((field) => (
                    <tr key={field.key as string} className="border-t border-gray-900/50">
                      <td className="p-2 text-xs uppercase tracking-wide text-muted-foreground">{field.label}</td>
                      {contacts.map((contact) => (
                        <td key={`${field.key}-${contact.id}`} className="p-2 align-top">
                          <label className="flex items-start gap-2 text-sm text-foreground/80">
                            <input
                              type="radio"
                              name={`merge-${field.key}`}
                              className="mt-1 h-3 w-3 accent-amber-400"
                              checked={(selectedFields[field.key as string] ?? primaryContact?.id ?? contacts[0].id) === contact.id}
                              onChange={() => handleSelect(field.key as string, contact.id)}
                            />
                            <span>{(contact[field.key] as string) ?? "—"}</span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button type="button" variant="secondary" onClick={handleApprove} disabled={merging}>
                {merging ? "Merging…" : "Approve merge"}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Reject
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MergeContactDrawer;
